import { Injectable, signal, computed } from '@angular/core';
import { environment } from '../../environments/environment';

export interface AdminApiConfig {
  baseUrl: string;
  wsUrl: string;
  language?: string; // Admin's preferred language for support conversations
}

/**
 * Centralized service for managing admin API configuration.
 * Allows runtime configuration of API endpoints and WebSocket URLs.
 */
@Injectable({ providedIn: 'root' })
export class AdminConfigService {
  private readonly config = signal<AdminApiConfig>({
    baseUrl: environment.apiOrigin,
    wsUrl: environment.apiOrigin.replace(/^http/, 'ws'),
    language: 'en'
  });

  /**
   * Current base URL for HTTP API requests
   */
  readonly baseUrl = computed(() => this.config().baseUrl);

  /**
   * Current base URL for public API requests (media, etc)
   */
  readonly publicBaseUrl = computed(() => environment.publicApiOrigin);

  /**
   * Current base URL for the main frontend application
   */
  readonly mainAppUrl = computed(() => environment.mainAppUrl);

  /**
   * Current WebSocket URL for real-time connections
   */
  readonly wsUrl = computed(() => this.config().wsUrl);

  /**
   * Current admin's preferred language for support conversations
   */
  readonly language = computed(() => this.config().language || 'en');

  /**
   * Update the API configuration
   * @param newConfig Partial or complete configuration object
   */
  setConfig(newConfig: Partial<AdminApiConfig>): void {
    this.config.update(current => ({
      ...current,
      ...newConfig
    }));
  }

  /**
   * Set the base URL (automatically derives WebSocket URL)
   * @param url Base URL (e.g., 'http://localhost:3501' or 'https://api.example.com')
   */
  setBaseUrl(url: string): void {
    const wsUrl = url.replace(/^http/, 'ws');
    this.setConfig({ baseUrl: url, wsUrl });
  }

  /**
   * Load configuration from localStorage if available
   */
  loadFromStorage(): void {
    try {
      const stored = localStorage.getItem('admin-api-config');
      if (stored) {
        const parsed = JSON.parse(stored) as AdminApiConfig;
        // Only load preferences, ignore connection settings to enforce environment config
        if (parsed.language) {
          this.setConfig({ language: parsed.language });
        }
      }
    } catch (error) {
      console.warn('Failed to load admin config from storage:', error);
    }
  }

  /**
   * Save current configuration to localStorage
   */
  saveToStorage(): void {
    try {
      // Only save preferences
      const preferences = { language: this.config().language };
      localStorage.setItem('admin-api-config', JSON.stringify(preferences));
    } catch (error) {
      console.warn('Failed to save admin config to storage:', error);
    }
  }

  /**
   * Set admin's preferred language
   */
  setLanguage(language: string): void {
    this.setConfig({ language });
  }

  /**
   * Reset to default configuration
   */
  reset(): void {
    this.setConfig({
      baseUrl: environment.apiOrigin,
      wsUrl: environment.apiOrigin.replace(/^http/, 'ws'),
      language: 'en'
    });
    localStorage.removeItem('admin-api-config');
  }
}
